import { format, formatDistanceToNow } from 'date-fns';
import {
  FileText,
  FolderOpen,
  Target,
  BarChart3,
  MessageSquare,
  Users,
  Search,
  Download,
  Upload,
  Trash2,
  Edit,
  Plus,
  CreditCard,
  LogIn,
  Shield,
  Bell,
  Loader2,
  Activity,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuditLog, formatAuditAction, formatAuditEntity } from '@/features/dashboard/hooks/useAudit';
import type { AuditLogEntry } from '@/types/database';

// =========================================
// Icon mapping
// =========================================

function getActionIcon(action: string, entityType: string) {
  const iconClass = 'h-4 w-4';

  switch (action) {
    case 'create':
      return <Plus className={iconClass} />;
    case 'delete':
      return <Trash2 className={iconClass} />;
    case 'update':
      return <Edit className={iconClass} />;
    case 'upload':
      return <Upload className={iconClass} />;
    case 'export':
      return <Download className={iconClass} />;
    case 'login':
    case 'logout':
      return <LogIn className={iconClass} />;
    case 'purchase':
      return <CreditCard className={iconClass} />;
    case 'invite':
      return <Users className={iconClass} />;
    case 'search':
      return <Search className={iconClass} />;
    case 'resolve':
      return <Shield className={iconClass} />;
  }

  switch (entityType) {
    case 'source':
      return <FileText className={iconClass} />;
    case 'folder':
      return <FolderOpen className={iconClass} />;
    case 'criteria':
      return <Target className={iconClass} />;
    case 'evaluation':
      return <BarChart3 className={iconClass} />;
    case 'chat':
      return <MessageSquare className={iconClass} />;
    case 'team':
      return <Users className={iconClass} />;
    case 'alert':
      return <Bell className={iconClass} />;
    default:
      return <Activity className={iconClass} />;
  }
}

function getActionColor(action: string): string {
  switch (action) {
    case 'create':
    case 'upload':
    case 'import':
      return 'bg-emerald-50 text-emerald-600';
    case 'delete':
      return 'bg-rose-50 text-rose-600';
    case 'update':
    case 'resolve':
      return 'bg-amber-50 text-amber-600';
    case 'evaluate':
    case 'chat':
      return 'bg-indigo-50 text-indigo-600';
    case 'purchase':
      return 'bg-violet-50 text-violet-600';
    case 'export':
      return 'bg-sky-50 text-sky-600';
    default:
      return 'bg-slate-50 text-slate-600';
  }
}

// =========================================
// Format description from details
// =========================================

function getDescription(entry: AuditLogEntry): string {
  const action = formatAuditAction(entry.action);
  const entity = formatAuditEntity(entry.entity_type);
  const details = entry.details as Record<string, unknown> | null;

  const name = details?.name as string | undefined;
  if (name) {
    return `${action} ${entity}: ${name}`;
  }

  return `${action} ${entity}`;
}

// =========================================
// AuditTrail Component
// =========================================

interface AuditTrailProps {
  limit?: number;
}

export function AuditTrail({ limit = 15 }: AuditTrailProps) {
  const { data: entries, isLoading } = useAuditLog(limit);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Recent Activity</CardTitle>
        <Activity className="h-4 w-4 text-slate-400" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : !entries?.length ? (
          <div className="text-center py-8">
            <Activity className="h-10 w-10 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No activity yet</p>
            <p className="text-xs text-slate-400 mt-1">
              Your actions will appear here
            </p>
          </div>
        ) : (
          <TooltipProvider>
            <div className="space-y-1">
              {entries.map((entry) => (
                <AuditEntry key={entry.id} entry={entry} />
              ))}
            </div>
          </TooltipProvider>
        )}
      </CardContent>
    </Card>
  );
}

// =========================================
// Single Audit Entry
// =========================================

function AuditEntry({ entry }: { entry: AuditLogEntry }) {
  const description = getDescription(entry);
  const colorClass = getActionColor(entry.action);
  const timeAgo = formatDistanceToNow(new Date(entry.created_at), { addSuffix: true });
  const exactTime = format(new Date(entry.created_at), 'MMM d, yyyy HH:mm:ss');

  return (
    <div className="flex items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-slate-50">
      <div className={`flex-shrink-0 rounded-lg p-1.5 ${colorClass}`}>
        {getActionIcon(entry.action, entry.entity_type)}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700 truncate">{description}</p>
      </div>

      <Tooltip>
        <TooltipTrigger asChild>
          <span className="flex-shrink-0 text-xs text-slate-400 whitespace-nowrap">
            {timeAgo}
          </span>
        </TooltipTrigger>
        <TooltipContent side="left">
          <p>{exactTime}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
